// Cliente + mapper para el módulo de Proyectos del backend real (backend/core).
// El backend fusiona Park + RenewableProject + ProjectStatusProject en una
// sola entidad `Project` (decisión de negocio ya tomada). El frontend
// TODAVÍA espera las formas viejas y separadas (`parks`, `renewableProjects`,
// `costCenters`, etc.) en muchos componentes — `mapProjectsResponse` traduce
// la respuesta nueva a esas formas viejas para no tener que tocar cada
// componente todavía. `project.id` se reusa como `parkId` Y como el `id` del
// renewableProject sintético (antes eran ids distintos vinculados por
// `renewableProjects[].parkId` — acá colapsan al mismo valor, sigue siendo
// consistente porque todo lo que compara `parkId === renewableProject.id`
// sigue dando true).

import { coreApi } from './coreClient';

export const projectsApi = {
  list: () => coreApi.get('/projects'),
  get: (id) => coreApi.get(`/projects/${id}`),
  create: (payload) => coreApi.post('/projects', payload),
  update: (id, payload) => coreApi.patch(`/projects/${id}`, payload),
  addArea: (id, payload) => coreApi.post(`/projects/${id}/areas`, payload),
  addPackage: (id, payload) => coreApi.post(`/projects/${id}/packages`, payload),
  updateBudget: (id, payload) => coreApi.patch(`/projects/${id}/budget`, payload),
  upsertAllocation: (id, payload) => coreApi.post(`/projects/${id}/budget/allocations`, payload),
  removeAllocation: (id, allocationId) => coreApi.delete(`/projects/${id}/budget/allocations/${allocationId}`),
  upsertIncome: (id, payload) => coreApi.post(`/projects/${id}/incomes`, payload),
  updateTimeline: (id, payload) => coreApi.patch(`/projects/${id}/timeline`, payload),
  addTimelineTask: (id, payload) => coreApi.post(`/projects/${id}/timeline/tasks`, payload),
  updateStatusItem: (itemId, payload) => coreApi.patch(`/projects/status-items/${itemId}`, payload),
  addPermit: (id, payload) => coreApi.post(`/projects/${id}/permits`, payload),
  updatePermit: (id, permitId, payload) => coreApi.patch(`/projects/${id}/permits/${permitId}`, payload),
  addRisk: (id, payload) => coreApi.post(`/projects/${id}/risks`, payload),
  updateRisk: (id, riskId, payload) => coreApi.patch(`/projects/${id}/risks/${riskId}`, payload),
  addMilestone: (id, payload) => coreApi.post(`/projects/${id}/milestones`, payload),
  updateMilestone: (id, milestoneId, payload) => coreApi.patch(`/projects/${id}/milestones/${milestoneId}`, payload),
  decideApproval: (id, gate, approved) => coreApi.patch(`/projects/${id}/approval/${gate}`, { approved }),
  updateStage: (id, stage) => coreApi.patch(`/projects/${id}/stage`, { stage })
};

const toLowerStage = (stage) => (stage || 'originacion').toLowerCase();

export const mapProjectsResponse = (projects) => {
  const parks = [];
  const renewableProjects = [];
  const costCenters = [];
  const projectBudgets = [];
  const projectTimelines = [];
  const renewablePermits = [];
  const renewableRisks = [];
  const renewableMilestones = [];
  const projectStatusItems = [];

  (projects || []).forEach((project) => {
    const administrators = (project.team || [])
      .filter((member) => member.role === 'ADMINISTRATOR')
      .map((member) => member.userId);
    const fieldTeam = (project.team || [])
      .filter((member) => member.role === 'FIELD_TEAM')
      .map((member) => member.userId);
    const areaManagerIds = (project.team || [])
      .filter((member) => member.role === 'AREA_MANAGER')
      .map((member) => member.userId);

    parks.push({
      id: project.id,
      name: project.name,
      code: project.code,
      managerId: project.projectManagerId || null,
      areas: (project.areas || []).map((area) => ({
        id: area.id,
        name: area.name,
        costCenterId: area.costCenters?.[0]?.id || null,
        areaManagerId: area.areaManagerId || null
      })),
      packages: (project.packages || []).map((pkg) => ({ id: pkg.id, code: pkg.code, name: pkg.name })),
      team: {
        projectManagerId: project.projectManagerId || null,
        administrators,
        controllerId: project.controllerId || null,
        fieldTeam,
        areaManagers: areaManagerIds
      }
    });

    (project.areas || []).forEach((area) => {
      (area.costCenters || []).forEach((cc) => {
        costCenters.push({
          id: cc.id,
          code: cc.code,
          name: cc.name,
          parkId: project.id,
          areaId: area.id,
          description: cc.description || ''
        });
      });
    });

    renewableProjects.push({
      id: project.id,
      parkId: project.id,
      name: project.name,
      technology: project.technology || '',
      region: project.region || '',
      commune: project.commune || '',
      mw: project.capacityMw != null ? Number(project.capacityMw) : 0,
      stage: toLowerStage(project.stage),
      rtbProgress: project.rtbProgress || 0,
      responsibleId: project.projectManagerId || null,
      approval: {
        projectManager: (project.approvals || []).some(
          (a) => a.stage === 'PROJECT_MANAGER' && a.status === 'APROBADA'
        ),
        purchasingManager: (project.approvals || []).some(
          (a) => a.stage === 'PURCHASING_MANAGER' && a.status === 'APROBADA'
        )
      },
      coordinates:
        project.latitude != null && project.longitude != null
          ? { lat: Number(project.latitude), lng: Number(project.longitude) }
          : null
    });

    if (project.budget) {
      projectBudgets.push({
        parkId: project.id,
        currency: project.budget.currency,
        total: Number(project.budget.total || 0),
        allocations: (project.budget.allocations || []).map((a) => ({
          id: a.id,
          name: a.name,
          category: a.category,
          amount: Number(a.amount || 0)
        }))
      });
    }

    if (project.timeline) {
      projectTimelines.push({
        parkId: project.id,
        baselineStart: project.timeline.baselineStart,
        baselineEnd: project.timeline.baselineEnd,
        tasks: (project.timeline.tasks || []).map((t) => ({
          id: t.id,
          partida: t.partida,
          subPartida: t.subPartida,
          name: t.name,
          start: t.start,
          end: t.end,
          progress: t.progress,
          owner: t.owner
        }))
      });
    }

    (project.permits || []).forEach((permit) => {
      renewablePermits.push({ ...permit, projectId: project.id });
    });
    (project.risks || []).forEach((risk) => {
      renewableRisks.push({ ...risk, projectId: project.id });
    });
    (project.milestones || []).forEach((milestone) => {
      renewableMilestones.push({ ...milestone, projectId: project.id });
    });
    (project.statusItems || []).forEach((item) => {
      projectStatusItems.push({ ...item, projectId: project.id });
    });
  });

  return {
    parks,
    renewableProjects,
    costCenters,
    projectBudgets,
    projectTimelines,
    renewablePermits,
    renewableRisks,
    renewableMilestones,
    projectStatusItems
  };
};
